/**
 * Organisation perimeter resolution shared by kpiService (KPI widgets + KPI grid) and spsService.
 *
 * Both used to keep their own copy of the ancestor expansion, and only kpiService knew about org
 * sharing — which is exactly how the "501 / 501 A" scope divergence with the BO appeared.
 */

/**
 * Keys a flat organisation collection by numeric id, the shape every helper below expects.
 *
 * @param {Array<object>} organisations ecosVM.organisations (or the cached API payload)
 * @returns {Map<number, object>}
 */
export function buildOrganisationById(organisations) {
	return new Map((organisations || []).map((org) => [Number(org.id), org]));
}

/**
 * True when `candidateId` is anywhere up `org`'s parent chain. Cycle-safe.
 *
 * @param {number} candidateId
 * @param {object} org
 * @param {Map<number, object>} organisationById
 * @returns {boolean}
 */
export function isAncestorOf(candidateId, org, organisationById) {
	const visited = new Set();
	let current = org;
	while (current?.parentId) {
		const parentId = Number(current.parentId);
		if (!Number.isFinite(parentId) || parentId <= 0 || visited.has(parentId)) break;
		visited.add(parentId);
		if (parentId === candidateId) return true;
		current = organisationById.get(parentId);
	}
	return false;
}

/**
 * The user's own organisation plus every descendant — i.e. the organisations whose stores are
 * genuinely theirs (Rattachés + Hérités), as opposed to ones only visible through an org-sharing
 * agreement (Partagés). Returns null when the user's organisation is unknown, which callers must
 * treat as "no restriction" rather than "nothing in scope".
 *
 * @param {number} userOrgId
 * @param {Map<number, object>} organisationById
 * @returns {Set<number>|null}
 */
export function buildUserOrgStrictScope(userOrgId, organisationById) {
	if (!userOrgId || userOrgId <= 0 || !organisationById.has(userOrgId)) return null;

	const scope = new Set([userOrgId]);
	for (const [id, organisation] of organisationById) {
		if (isAncestorOf(userOrgId, organisation, organisationById)) scope.add(Number(id));
	}
	return scope;
}

/**
 * A store is "Partagé" when it has organisations and NONE of its raw organisations sits in the
 * user's strict scope — it only reaches them through an org-sharing agreement. Deliberately based on
 * the store's raw organisations rather than on the sharing pool: on DEMOCLIENT (user Paris/80) that
 * is what separates the 53 own stores from the 33 that only arrive via organisation 134's sharing.
 *
 * A store with no organisation at all was not brought in by a sharing, so it is never "Partagé".
 * This also settles the "Hérité AND Partagé" ambiguity (spec RG-08): an inherited store has, by
 * definition, an organisation inside the user's subtree, so inheritance wins on its own.
 *
 * @param {Array<number|string>} deliveryLocationOrganisations dl.organisations
 * @param {Set<number>|null} userOrgStrictScope from buildUserOrgStrictScope()
 * @returns {boolean}
 */
export function isDeliveryLocationShared(deliveryLocationOrganisations, userOrgStrictScope) {
	if (!userOrgStrictScope) return false;
	const assignedOrganisationIds = (Array.isArray(deliveryLocationOrganisations) ? deliveryLocationOrganisations : []).map(Number);
	if (assignedOrganisationIds.length === 0) return false;
	return !assignedOrganisationIds.some((organisationId) => userOrgStrictScope.has(organisationId));
}

/**
 * Builds Map<organisationId, Set<partnerOrganisationId>> from ecosVM.organisationSharings.
 *
 * A sharing entry lists the organisations a parent organisation shares its stores with, and the
 * backend persists both directions (46 -> [47] and 47 -> [46]), same assumption as the legacy
 * salesHelper.isOrganizationShared(). Every member of a pool sees every other member's stores, so
 * the map is built symmetric.
 */
export function buildOrgSharingExpansionMap(sharings) {
	const map = new Map();
	for (const sharing of sharings || []) {
		const parentId = Number(sharing.parentOrganisationId);
		const childIds = (sharing.organisations || []).map(Number);
		const poolMembers = [parentId, ...childIds].filter((id) => Number.isFinite(id) && id > 0);
		for (const memberId of poolMembers) {
			if (!map.has(memberId)) map.set(memberId, new Set());
			for (const otherId of poolMembers) {
				if (otherId !== memberId) map.get(memberId).add(otherId);
			}
		}
	}
	return map;
}

/**
 * Resolves the set of organisation ids a store may be assigned to for it to belong to the selected
 * organisation perimeter.
 *
 * Two widenings can apply:
 * - ancestors of each selected org (`includeAncestors`), so a store assigned to "Paris" stays in scope
 *   when filtering by its child org "CS1". This is what makes kpiService/spsService match the BO's own
 *   Détail des KPIs / Analyse produit report perimeter (#23326) — keep it on there. It assumes a store
 *   tagged at a broad org is the rare exception; on a customer where most stores are tagged at the root
 *   (INNOCENT FRANCE: 55% tagged "National" directly), turning it on for a plain store-list filter
 *   makes any selection match almost everything, so the delivery-location filters popup passes
 *   `includeAncestors: false` — its tree already lets the user pull in a whole branch explicitly via
 *   the "select branch" action, which adds every descendant id to the selection up front;
 * - each selected org's sharing partners, so filtering by "501" reports the park the BO reports for
 *   it — its own stores PLUS the stores "501 A" shares with it. Partners are added flat, never
 *   expanded up their own ancestor chain: a partner shares its own stores, not its parent branch's.
 *   Skipped when the "Exclure les organisations partagées" toggle is on.
 *
 * @param {Array<number|string>} organisationIds selected organisation ids
 * @param {Map<number, object>} organisationById ecosVM.organisations keyed by numeric id
 * @param {Map<number, Set<number>>} orgSharingExpansionMap from buildOrgSharingExpansionMap()
 * @param {boolean} excludeSharedOrganisations
 * @param {boolean} includeAncestors
 * @returns {Set<number>}
 */
export function expandOrganisationPerimeter(organisationIds, organisationById, orgSharingExpansionMap, excludeSharedOrganisations = false, includeAncestors = true) {
	const expanded = new Set();
	for (const organisationId of organisationIds || []) {
		const numericId = Number(organisationId);
		if (!Number.isFinite(numericId) || numericId <= 0) continue;
		expanded.add(numericId);

		if (includeAncestors) {
			let currentOrg = organisationById?.get(numericId);
			const visited = new Set([numericId]);
			while (currentOrg?.parentId) {
				const parentId = Number(currentOrg.parentId);
				if (!Number.isFinite(parentId) || parentId <= 0 || visited.has(parentId)) break;
				visited.add(parentId);
				expanded.add(parentId);
				currentOrg = organisationById?.get(parentId);
			}
		}

		if (excludeSharedOrganisations) continue;
		const partners = orgSharingExpansionMap?.get(numericId);
		if (partners) {
			for (const partnerId of partners) expanded.add(partnerId);
		}
	}
	return expanded;
}
